import { useEffect } from 'react';

import { SubmitHandler, useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useNavigate, useParams } from 'react-router-dom';

import { AppRoutes } from 'constants/routes-paths.constants';
import { REQUIRED_VALIDATION } from 'constants/validation-rules.constants';
import { useBeforeRouteChange } from 'hooks/useBeforeRouteChange';
import { CustomerRequestStatusInput } from 'interfaces/customer-request-statuses.interfaces';
import {
  asyncCreateCustomerRequestStatus,
  asyncGetCustomerRequestStatus,
  asyncUpdateCustomerRequestStatus,
  unsetCustomerRequestStatus,
} from 'modules/customer-request-statuses/action';
import { selectCustomerRequestStatus } from 'modules/customer-request-statuses/selectors';
import { useAppDispatch, useAppSelector } from 'modules/store';
import { Card } from 'shared-components/Card';
import { Input } from 'shared-components/Input';
import { ItemNavigation } from 'shared-components/ItemNavigation';

export const CustomerRequestStatusForm = () => {
  const { id } = useParams();

  const navigate = useNavigate();

  const dispatch = useAppDispatch();

  const customerRequestStatus = useAppSelector(selectCustomerRequestStatus);

  const { formatMessage } = useIntl();

  const {
    reset,
    handleSubmit,
    register,
    formState: { errors },
  } = useForm<CustomerRequestStatusInput>();

  const onSubmit: SubmitHandler<CustomerRequestStatusInput> = (data) => {
    dispatch(id ? asyncUpdateCustomerRequestStatus({ id: +id, ...data }) : asyncCreateCustomerRequestStatus(data))
      .unwrap()
      .then(() => {
        navigate(AppRoutes.customerRequestStatuses);
      })
      .catch(() => 'error');
  };

  useBeforeRouteChange(() => dispatch(unsetCustomerRequestStatus()));

  useEffect(() => {
    if (id) {
      dispatch(asyncGetCustomerRequestStatus(id));
    }
  }, [dispatch, id]);

  useEffect(() => {
    if (id) {
      reset({ name: customerRequestStatus?.name ?? '' });
    }
  }, [id, reset, customerRequestStatus]);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Card title='general_info' wrapperClassName='w-full md:w-1/2'>
        <div className='flex flex-col gap-4'>
          <Input
            {...register('name', REQUIRED_VALIDATION)}
            error={errors['name']?.message}
            label='title'
            isRequired
            placeholder={formatMessage({ id: 'title' })}
            data-test='customer-request-status-name'
          />
          <ItemNavigation navigateBack={AppRoutes.customerRequestStatuses} />
        </div>
      </Card>
    </form>
  );
};
